import type { ComponentChildren } from 'preact';
import { EdgeScroll } from './EdgeScroll';
import { fmtNum } from './ListFoot';

// Small shared pieces from the style guide (board 0), using the design tokens.

export type ReadType = 'light' | 'absorbing' | 'demanding' | 'study';

export const READ_TYPES: { key: ReadType; name: string; tip: string }[] = [
  { key: 'light', name: 'Light', tip: 'Doesn’t demand focused attention or long sessions' },
  { key: 'absorbing', name: 'Absorbing', tip: 'Better with more attention and/or longer sessions' },
  { key: 'demanding', name: 'Demanding', tip: 'Needs focus; could be slower going' },
  { key: 'study', name: 'Study', tip: 'Close examination; to learn or for research' },
];

export const readTypeName = (t: ReadType | null | undefined) =>
  t ? READ_TYPES.find((r) => r.key === t)!.name : 'No read type';

/** A count inside a chip is an aside in square brackets: "Library [9]", "Paper [1,234]". */
export const chipCount = (n: number | undefined) => (n === undefined ? '' : ` [${fmtNum(n)}]`);

/**
 * Filter chip (consistency pass R3 P2, Kristi Nov 2): it narrows the list you're on. Loose and
 * outlined, no track behind it; filled teal while on; tap again to turn it off. Several can be on
 * at once, and none on means everything (so there's never an "All" chip). The count sits in
 * [brackets], counted within what the Filters panel leaves (R7 N3); `need` adds the needs-you dot
 * with its tip (R7 N9). A read-type chip carries its value dot.
 */
export function Chip({
  label,
  count,
  on,
  onToggle,
  readType,
  need,
  small,
  title,
  sug,
  plain,
  disabled,
  worth,
}: {
  label: ComponentChildren;
  count?: number;
  on: boolean;
  onToggle: () => void;
  readType?: ReadType | 'none';
  /** It leads to something that needs you: the dot, with this tip ("Needs you: 7 with no shelf yet"). */
  need?: string;
  /** It leads to something worth a look: the ring, with this tip ("Worth a look: 390 not placed yet"; filters review F2). */
  worth?: string;
  small?: boolean;
  title?: string;
  /** While on, it's a suggestion still waiting (Review): pale and dashed (`sug`) instead of filled. */
  sug?: boolean;
  /** Not a toggle, so no aria-pressed: a one-tap action ("7 days") or a name that fills a box. */
  plain?: boolean;
  disabled?: boolean;
}) {
  return (
    <button type="button" class={`chip${small ? ' small' : ''}${on ? (sug ? ' sug' : ' on') : ''}`} aria-pressed={plain ? undefined : on} title={title} disabled={disabled} onClick={onToggle}>
      {need ? <NeedDot tip={need} /> : worth ? <WorthRing tip={worth} /> : null}
      {readType ? <span class={`rt-dot rt-${readType}`} aria-hidden="true" /> : null}
      {label}
      {count !== undefined ? <span class="chip-count">{chipCount(count)}</span> : null}
    </button>
  );
}

/**
 * A few choices in a form, one of them on (R4 D5: a few choices are chips, not a menu): Literary
 * genre, How you got it, Where it goes… Chips in a radio group; `clearable` lets a second tap on
 * the chosen one clear it. `children` go at the end of the row (a "# 12" place box, an Add field).
 * For narrowing a list use FilterChip; for choosing a view, Segmented.
 */
export function ChoiceChips<T extends string | number | boolean>({
  label,
  choices,
  value,
  onPick,
  clearable,
  small,
  children,
  lead,
  class: cls,
  pending,
  onAccept,
}: {
  label: string;
  choices: readonly (readonly [T, ComponentChildren])[];
  value: T | null | undefined;
  onPick: (v: T | null) => void;
  clearable?: boolean;
  small?: boolean;
  children?: ComponentChildren;
  /** Words at the start of the row ("Changed in Kindle:"). */
  lead?: ComponentChildren;
  /** An extra class on the row, for the screen's layout (bk-dialog-chips). */
  class?: string;
  /** The chosen one is a suggestion still waiting (Review): pale and dashed; tapping it calls `onAccept`. */
  pending?: boolean;
  onAccept?: () => void;
}) {
  return (
    <div class={`choice-chips${cls ? ` ${cls}` : ''}`} role="radiogroup" aria-label={label}>
      {lead}
      {choices.map(([v, text]) => {
        const on = value === v;
        const sug = on && pending;
        return (
          <button
            key={String(v)}
            type="button"
            role="radio"
            aria-checked={on}
            title={sug ? 'Suggested: tap to accept' : undefined}
            class={`chip${small ? ' small' : ''}${on ? (sug ? ' sug' : ' on') : ''}`}
            onClick={() => (sug && onAccept ? onAccept() : onPick(on && clearable ? null : v))}
          >
            {text}
          </button>
        );
      })}
      {children}
    </div>
  );
}

/** Several of a few, in a form (your genres, Not for me's reasons): Chips in a labelled group. */
export function ToggleChips({ label, choices, on, onChange, small = true, class: cls }: { label: string; choices: readonly string[]; on: readonly string[]; onChange: (next: string[]) => void; small?: boolean; class?: string }) {
  return (
    <div class={`choice-chips${cls ? ` ${cls}` : ''}`} role="group" aria-label={label}>
      {choices.map((c) => (
        <Chip key={c} label={c} small={small} on={on.includes(c)} onToggle={() => onChange(on.includes(c) ? on.filter((x) => x !== c) : [...on, c])} />
      ))}
    </div>
  );
}

/**
 * The filter chip a screen uses: a Chip that hides itself while its count is 0, unless it's on
 * (R3 P2: "Audible [0]" stays out of the way until it has copies).
 */
export function FilterChip(props: Parameters<typeof Chip>[0]) {
  if (props.count === 0 && !props.on) return null;
  return <Chip {...props} />;
}

/**
 * Between the signal chips and a view's quick chips when they share one row (filters review F1):
 * signals first, then this, then the browsing chips.
 */
export function ChipDivider() {
  return <span class="chip-divider" aria-hidden="true" />;
}

/**
 * A row of filter chips (R9 M5): it wraps when it fits in two lines (the default); a longer row
 * stays on one line and scrolls with the edge (`scroll`). `label` names the group for screen readers.
 */
export function ChipRow({ label, scroll, surface, children }: { label: string; scroll?: boolean; surface?: 'page' | 'panel' | 'card' | 'sheet'; children: ComponentChildren }) {
  const row = (
    <div class={`chip-row${scroll ? ' one-line' : ''}`} role="group" aria-label={label}>
      {children}
    </div>
  );
  return scroll ? (
    <EdgeScroll label={label} surface={surface}>
      {row}
    </EdgeScroll>
  ) : (
    row
  );
}

/**
 * A filter that's on, beside Filters [3] ▾ (R3 P6): a filled chip with ✕ that removes it
 * ("Mystery ✕", "Hide: Horror ✕"). Then Clear all (a link) and the result in words.
 */
export function RemovableChip({ label, hide, onRemove }: { label: string; hide?: boolean; onRemove: () => void }) {
  return (
    <button type="button" class="chip on removable" aria-label={`Remove ${hide ? 'hide ' : ''}${label}`} onClick={onRemove}>
      {hide ? `Hide: ${label}` : label}{' '}
      <span class="chip-x" aria-hidden="true">
        ✕
      </span>
    </button>
  );
}

/** The style guide's old name for RemovableChip. */
export const ActiveChip = RemovableChip;

/** Status pill: a fact about a book, never clickable. One style (tint, teal text). */
export function Pill({ children, quiet }: { children: ComponentChildren; quiet?: boolean }) {
  return <span class={`pill${quiet ? ' quiet' : ''}`}>{children}</span>;
}

/** Read type on a card: small regular-weight capitals in the corner (words carry the meaning). */
export function ReadTypeLabel({ type }: { type: ReadType | null | undefined }) {
  if (!type) return null;
  return (
    <span class="rt-label" title={READ_TYPES.find((r) => r.key === type)!.tip}>
      {readTypeName(type)}
    </span>
  );
}

/** Interest: 1–5 dots, tappable; tapping the current score clears it. */
export function InterestDots({ value, onChange, hints }: { value: number | null; onChange?: (v: number | null) => void; hints?: readonly string[] }) {
  return (
    <span class="dots" role={onChange ? 'radiogroup' : 'img'} aria-label={`Interest ${value ?? 'not set'}`}>
      {[1, 2, 3, 4, 5].map((n) =>
        onChange ? (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={hints ? `${n} · ${hints[n - 1]}` : `${n} of 5`}
            title={hints ? `${n} · ${hints[n - 1]}${value === n ? ' (tap again to clear)' : ''}` : undefined}
            class={`dot-btn${value !== null && n <= value ? ' on' : ''}`}
            onClick={() => onChange(value === n ? null : n)}
          />
        ) : (
          <span key={n} class={`dot-btn${value !== null && n <= value ? ' on' : ''}`} aria-hidden="true" />
        ),
      )}
    </span>
  );
}

/** A cover: the image when there is one, otherwise the title on a plain block. */
export function Cover({ title, url, size = 'md' }: { title: string; url?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  return url ? (
    <img class={`cover cover-${size}`} src={url} alt="" loading="lazy" />
  ) : (
    <span class={`cover cover-${size} cover-blank`} aria-hidden="true">
      {title}
    </span>
  );
}

/**
 * The teal "needs you" dot (Kristi, Oct 31): on a tab, a tile or a chip whose books or copies
 * need you, gone once they're done. Each dot leads to a list. Hidden from screen readers: the
 * words beside it say what needs doing. `tip` is the tooltip (a tab's "Needs you: …").
 */
export function NeedDot({ tip = 'Needs you' }: { tip?: string }) {
  return <span class="need-dot" aria-hidden="true" title={tip} />;
}

/**
 * The worth-a-look ring (filters review F2, Kristi Nov 2): optional tidying, weaker than the dot.
 * It sits on a chip or a section heading only: never on a tab, and a row that's worth a look shows
 * its dashed value pill or grey words instead of the teal edge. Always with its tip.
 */
export function WorthRing({ tip }: { tip: string }) {
  return <span class="worth-ring" aria-hidden="true" title={tip} />;
}
